import Card from '../../components/card/Card';
import Icon from '../../components/icon/Icon';
import P from '../../components/texts/p/P';
import { legalEntities } from '../../mocks/LegalEntities';
import Input from '../../components/input/Input';
import Button from '../../components/button/Button';
import { useEffect, useRef, useState, useMemo } from 'react';
import {
  deleteClientDocument,
  downloadClientDocument,
  downloadApplicationDocument,
  downloadLufeCloudFile,
  getLufeCloudFiles,
  saveClientDocument,
  saveApplicationDocument,
} from '../../services/ClientService';
import { deleteApplicationFile } from '../../services/ApplicationService';
import { getOtherLegalEntitiesFileTypes } from '../../services/ApplicationService';
import Span from '../../components/texts/span/Span';
import { cloudDocument, documentData } from '../../types/interfaces';
import {
  getAcceptedExtensions,
  getAcceptedFormats,
  getAcceptedFormatsLabel,
  MAX_FILE_SIZE,
} from '../../utils/Constants';
import { isAcceptedFileFormat } from '../../utils/fileValidation';
import Spinner from '../../components/Spinner/spinner';

import Label from '../../components/texts/label/Label';
import DropDown from '../../components/dropdown/DropDown';
import { Toast, useToast } from '../../components/toast/toast';
import {
  LUFE_DEGRADED_TOAST_TITLE,
  shouldShowLufeDegradedToast,
} from '../../utils/lufeDegraded';
import useStatus from '../../hooks/useStatus';
import { getMimeType, removeFileExtension } from '../../utils/Utils';
import { isLufeCloudFileName } from '../../utils/isLufeCloudFileName';
import { reconcileDocuments } from '../../utils/documentMerge';
import {
  buildDocumentDedupKey,
  deduplicateDocumentsForSave,
  hasUnpersistedExtraDocuments,
  isInvalidExtraFile,
} from '../../utils/documentKey';
import { LocalDocument, normalizeFilename } from '../../utils/documentModel';
import BulkDocumentUploader from '../../components/bulkDocumentUploader';
import { FileWithType } from '../../components/bulkDocumentUploader/BulkDocumentUploader.types';
import TruncatedTextWithTooltip from '../../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import {
  FileRowContainer,
  DropdownWrapper,
  FileNameWrapper,
  FileNameLabelWrapper,
} from '../../components/bulkDocumentUploader/BulkDocumentUploader.styles';
import { useConfig } from '../../context/ConfigContext';
import { getOriginationStep2DocumentLabel } from '../../utils/originationDocumentLabels';
import {
  fillsOriginationRequiredSlot,
  findDocumentForRequiredSlot,
  BALANCE_DOCUMENT_ID,
  getRequiredDocumentsForLegalEntity,
  isBalancePdfDocumentType,
  PREVIOUS_BALANCE_DOCUMENT_LABEL,
} from '../../utils/originationRequiredDocuments';
import {
  isApprovedStatus,
  isFinishedOrRejectedStatus,
} from '../../utils/applicationStatusUtils';
import {
  clearLufeDocTombstone,
  isLufeDocTombstoned,
  setLufeDocTombstone,
} from '../../utils/lufeDocTombstone';

interface DocumentProps {
  documents: documentData[];
  onSave: Function;
  selectedLegalEntity: string;
  cuit: string;
  applicationId?: string;
  onPreviousClick: Function;
  isSaving: boolean;
  applicationStatus: number | string;
  lufeBalanceSourceYear?: number | null;
}

const Documents: React.FC<DocumentProps> = ({
  onSave,
  selectedLegalEntity,
  cuit,
  applicationId,
  documents,
  onPreviousClick,
  isSaving,
  applicationStatus,
  lufeBalanceSourceYear: persistedLufeBalanceSourceYear,
}) => {
  const config = useConfig();
  const isUruguayEnabled = config.isUruguayEnabled ?? false;
  const acceptedExtensions = useMemo(
    () => getAcceptedExtensions(isUruguayEnabled),
    [isUruguayEnabled]
  );
  const acceptedFormats = useMemo(
    () => getAcceptedFormats(isUruguayEnabled),
    [isUruguayEnabled]
  );
  const acceptedFormatsLabel = useMemo(
    () => getAcceptedFormatsLabel(isUruguayEnabled),
    [isUruguayEnabled]
  );

  const { isApproved, isRejected, isInCommittee, isFinished } = useStatus({
    applicationStatus,
  });

  const filteredRequiredDocuments = useMemo(
    () => getRequiredDocumentsForLegalEntity(selectedLegalEntity, legalEntities, config),
    [
      selectedLegalEntity,
      config.isOriginationPreviousBalanceEnabled,
      config.isUruguayEnabled,
    ]
  );

  const [files, setFiles] = useState<(documentData | null)[]>(() =>
    filteredRequiredDocuments
      .map((req) => findDocumentForRequiredSlot(documents, req.id))
      .filter((doc): doc is documentData => Boolean(doc))
  );
  const [fileIds, setFileIds] = useState<string[]>(() =>
    filteredRequiredDocuments
      .map((req) => findDocumentForRequiredSlot(documents, req.id))
      .filter((doc): doc is documentData => Boolean(doc))
      .map((doc) => doc.file_name)
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [extraFiles, setExtraFiles] = useState<LocalDocument[]>([]);
  const [extraFilesErrors, setExtraFilesErrors] = useState<string[]>([]);
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [cloudFiles, setCloudFiles] = useState<cloudDocument[]>([]);
  const [lufeBalanceSourceYear, setLufeBalanceSourceYear] = useState<number | undefined>(
    persistedLufeBalanceSourceYear ?? undefined
  );
  const [bulkUploadedFiles, setBulkUploadedFiles] = useState<FileWithType[]>(
    []
  );
  const [isUploadingPending, setIsUploadingPending] = useState<boolean>(false);
  const [deletedKeys, setDeletedKeys] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const prevSignature = useRef<string>('');
  const isHydrating = useRef<boolean>(false);
  const requiredUploadPromisesRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const pendingRequiredUploadsCountRef = useRef(0);
  const isSubmittingRef = useRef(false);
  const [pendingRequiredUploadsCount, setPendingRequiredUploadsCount] = useState(0);
  const [otherLegalEntities, setOtherLegalEntities] = useState<Array<{id: number; code: string; description: string}>>([]);

  const recalculateContinueDisabled = (pendingRequiredCount: number) => {
    const hasMissingExtraType =
      extraFiles.some((f) => !f.type && !f.isDeleted) ||
      bulkUploadedFiles.some(
        (f) => f.uploadStatus !== 'error' && (!f.type || f.type === null)
      );
    setIsContinueDisabled(pendingRequiredCount > 0 || hasMissingExtraType);
  };

  const trackRequiredUpload = (documentId: string, uploadPromise: Promise<unknown>) => {
    requiredUploadPromisesRef.current.set(documentId, uploadPromise);
    pendingRequiredUploadsCountRef.current += 1;
    setPendingRequiredUploadsCount(pendingRequiredUploadsCountRef.current);
    recalculateContinueDisabled(pendingRequiredUploadsCountRef.current);

    uploadPromise.finally(() => {
      requiredUploadPromisesRef.current.delete(documentId);
      pendingRequiredUploadsCountRef.current = Math.max(
        0,
        pendingRequiredUploadsCountRef.current - 1
      );
      setPendingRequiredUploadsCount(pendingRequiredUploadsCountRef.current);
      recalculateContinueDisabled(pendingRequiredUploadsCountRef.current);
    });

    uploadPromise.catch(() => undefined);
  };

  const generateId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const deletePreviousFileBestEffort = async (
    newFileName: string,
    fileType: string,
    existingFiles: Array<{ file_name?: string; type?: string } | null>
  ): Promise<void> => {
    if (!applicationId) return;

    if (isBalancePdfDocumentType(fileType)) {
      const seen = new Set<string>();
      for (const existingFile of existingFiles) {
        if (!existingFile?.file_name || existingFile.type !== fileType) continue;
        const dedupeKey = `${existingFile.type}::${existingFile.file_name}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);

        try {
          await deleteApplicationFile(
            cuit,
            applicationId,
            existingFile.file_name,
            existingFile.type
          );
        } catch (error) {
          console.warn(
            '[Documents] Failed to delete previous balance file (continuing with upload):',
            {
              fileType,
              newFileName,
              existingFileName: existingFile.file_name,
              error,
            }
          );
        }
      }
      return;
    }

    const newKey = buildDocumentDedupKey(fileType, newFileName);

    const existingFile = existingFiles.find((f) => {
      if (!f?.file_name || !f?.type) return false;
      if (!f.file_name.includes('.')) return false;
      const existingKey = buildDocumentDedupKey(f.type, f.file_name);
      return existingKey === newKey;
    });

    if (!existingFile?.file_name) return;

    try {
      await deleteApplicationFile(cuit, applicationId, existingFile.file_name, existingFile.type);
    } catch (error) {
      console.warn(
        '[Documents] Failed to delete previous file (continuing with upload):',
        { fileType, newFileName, existingFileName: existingFile.file_name, error }
      );
    }
  };

  // Fetch de tipos de documentos al montar el componente
  useEffect(() => {
    const fetchOtherLegalEntities = async () => {
      try {
        const response = await getOtherLegalEntitiesFileTypes();
        if (response.success && response.data) {
          setOtherLegalEntities(response.data);
        } else {
          useToast.error({
            title: 'Ocurrió un error al obtener los tipos de documentos.',
            id: 'file-types-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error('Error fetching other legal entities:', error);
        useToast.error({
          title: 'Ocurrió un error al obtener los tipos de documentos.',
          id: 'file-types-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    };

    fetchOtherLegalEntities();
  }, []);

  useEffect(() => {
    if (isUruguayEnabled) {
      setIsLoading(false);
      return;
    }
    if (
      isApprovedStatus(applicationStatus) ||
      isFinishedOrRejectedStatus(applicationStatus)
    ) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchCloudFiles = async () => {
      try {
        const response = await getLufeCloudFiles(cuit);
        if (isCancelled) {
          return;
        }

        if (shouldShowLufeDegradedToast(response)) {
          useToast.info({
            title: LUFE_DEGRADED_TOAST_TITLE,
            id: 'save-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }

        if (!response.success) {
          return;
        }

        const requiredIds = filteredRequiredDocuments.map((doc: any) => doc.id);

        setFiles((prev) => {
          const updated = [...prev];
          let hasNew = false;

          (response.data || []).forEach((doc) => {
            const isRequired = requiredIds.includes(doc.type);
            const alreadyInFiles = prev.some((f) => f?.type === doc.type);
            const isTombstoned =
              applicationId != null &&
              isLufeDocTombstoned(applicationId, doc.type);

            if (isRequired && !alreadyInFiles && !isTombstoned) {
              updated.push({
                file: doc.documentId,
                file_name: doc.documentId,
                type: doc.type,
                is_basic_data: true,
              } as any);
              hasNew = true;
            }
          });

          return hasNew ? updated : prev;
        });

        if (!isCancelled) {
          const cloudDocs = response.data || [];
          setCloudFiles(cloudDocs);
          const balanceDoc = cloudDocs.find((doc) => doc.type === BALANCE_DOCUMENT_ID);
          const sourceYear =
            balanceDoc?.sourcePeriodYear ?? balanceDoc?.source_period_year;
          if (sourceYear != null) {
            setLufeBalanceSourceYear(Number(sourceYear));
          }
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchCloudFiles();

    return () => {
      isCancelled = true;
    };
  }, [
    applicationStatus,
    applicationId,
    cuit,
    selectedLegalEntity,
    filteredRequiredDocuments,
    isUruguayEnabled,
  ]);

  // Reconstruir el estado local de documentos (extraFiles) desde backend + locales
  useEffect(() => {
    isHydrating.current = true;

    const base: LocalDocument[] = [
      ...(documents || [])
        .filter(
          (doc) =>
            doc.type &&
            doc.file_name &&
            !isBalancePdfDocumentType(doc.type) &&
            !filteredRequiredDocuments.some((req) =>
              fillsOriginationRequiredSlot(doc, req.id)
            )
        )
        .map((doc, idx) => {
          const baseName = normalizeFilename(doc.file_name || '');
          const isDeleted = deletedKeys.some((k) => normalizeFilename(k) === baseName);
          return {
            id: doc.file_name || doc.type || `${idx}`,
            type: doc.type || '',
            label: otherLegalEntities.find((o) => o.code === doc.type)?.description || '',
            asset_file_path: doc.file_name,
            file_name: doc.file_name,
            isPending: false,
            isDeleted,
            order: idx,
          } as LocalDocument;
        }),
    ];

    setExtraFiles((prev) =>
      reconcileDocuments({
        base,
        locals: prev,
      })
    );
  }, [documents, deletedKeys, filteredRequiredDocuments]);

  const handleFileDelete = async (index: string) => {
    const filename = files.find((item) => item?.type === index)?.file_name;
    const fileIndex = files.findIndex((item) => item?.type === index);

    if (!filename || fileIndex === -1) return;

    if (applicationId) {
      const response = await deleteApplicationFile(cuit, applicationId, filename, index);
      if (!response.success) {
        useToast.error({
          title: 'Error al eliminar el documento',
          id: 'documents-delete-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        return;
      }
    } else {
      try {
        await deleteClientDocument(cuit, filename);
      } catch (error) {
        console.warn('[Documents] Failed to delete file:', { filename, error });
        useToast.error({
          title: 'Error al eliminar el documento',
          id: 'documents-delete-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        return;
      }
    }

    if (applicationId) {
      setLufeDocTombstone(applicationId, index);
    }

    const newFiles = [...files];
    newFiles[fileIndex] = null;
    setFiles(newFiles);
    const newErrors = [...errors];
    newErrors[fileIndex] = null;
    setErrors(newErrors);
    setHasUnsavedChanges(true);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentId: string
  ) => {
    setIsContinueDisabled(true);
    const file = event.target.files?.[0] || null;
    let errorMessage: string = '';
    const newErrors = [...errors];
    if (file) {
      if (isBalancePdfDocumentType(documentId)) {
        const isPdf =
          file.type === 'application/pdf' ||
          file.name.toLowerCase().endsWith('.pdf');
        if (!isPdf) {
          errorMessage =
            documentId === '103'
              ? `El ${PREVIOUS_BALANCE_DOCUMENT_LABEL} solo acepta archivos PDF.`
              : 'El Último Estado Contable solo acepta archivos PDF.';
        }
      } else if (!isAcceptedFileFormat(file, acceptedFormats, acceptedExtensions)) {
        errorMessage = `El formato de archivo solo puede ser: ${acceptedFormatsLabel}`;
      }
      if (!errorMessage && file.size > MAX_FILE_SIZE) {
        errorMessage = 'El tamaño del archivo no puede ser mayor de 20 MB';
      }
      const errorIndex =
        filteredRequiredDocuments.findIndex((doc: any) => doc.id === documentId);

      if (errorMessage) {
        const inputElement = document.getElementById(
          `file-input-${documentId}`
        ) as HTMLInputElement;
        inputElement.value = '';
        if (errorIndex !== -1) {
          newErrors[errorIndex] = errorMessage;
        }
        setErrors(newErrors);
      } else {
        newErrors[errorIndex] = '';
        setErrors(newErrors);

        void (async () => {
          await deletePreviousFileBestEffort(file.name, documentId, [
            ...files,
            ...(documents || []),
          ]);

          const fileName = removeFileExtension(file.name);
          const formData = new FormData();
          formData.append('file', file);
          formData.append('type', documentId);
          formData.append('fileName', fileName);
          formData.append('isBasicData', 'true');
          const rawPromise = applicationId
            ? saveApplicationDocument(cuit, applicationId, formData)
            : saveClientDocument(cuit, formData);
          rawPromise.catch(() => undefined);
          const savePromise = rawPromise.then((response) => {
            if (!response.success) {
              useToast.error({
                title: 'Error al cargar uno o más documentos requeridos',
                id: 'documents-upload-toast',
                description: '',
                closeButton: true,
                autoClose: 5000,
              });
              return;
            }
            const fileIdsArray = [...fileIds];
            fileIdsArray.push(response.data);
            setFileIds(fileIdsArray);
            const newFiles = [...files];
            const newErrorsAfterSave = [...errors];
            const fileToSend = {
              file: file?.name,
              file_name: response.data,
              type: documentId,
              is_basic_data: true,
            };
            const fileIndex = files.findIndex(
              (item) => item?.type === documentId
            );

            if (fileIndex === -1) {
              newFiles.push(fileToSend);
              newErrorsAfterSave.push('');
            } else {
              newFiles[fileIndex] = fileToSend;
              newErrorsAfterSave[fileIndex] = '';
            }
            if (errorIndex !== -1) {
              newErrorsAfterSave[errorIndex] = '';
            }
            setFiles(newFiles);
            setErrors(newErrorsAfterSave);
          });
          trackRequiredUpload(documentId, savePromise);
          savePromise.catch(() => {
            useToast.error({
              title: 'Error al cargar uno o más documentos requeridos',
              id: 'documents-upload-toast',
              description: '',
              closeButton: true,
              autoClose: 5000,
            });
          });
        })();
      }
    }
  };

  const handleDownloadFile = (index: string) => {
    const filename = files.find((item) => item?.type === index)?.file_name;
    if (isLufeCloudFileName(filename)) {
      downloadCloudDocument(filename);
    } else {
      const downloadPromise = applicationId
        ? downloadApplicationDocument(cuit, applicationId, filename)
        : downloadClientDocument(cuit, filename);
      
      downloadPromise.then((response) => {
        if (response.success) {
          const mimeType = getMimeType(filename);
          const fileBlob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(fileBlob);

          if (
            mimeType.startsWith('application/pdf') ||
            mimeType.startsWith('image/')
          ) {
            window.open(url, '_blank');
          } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          }
        }
      });
    }
  };

  const downloadCloudDocument = (url: string) => {
    downloadLufeCloudFile(cuit, url).then((response) => {
      if (response.success) {
        const fileBlob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(fileBlob);
        window.open(url, '_blank');
      }
    });
  };

  const handleSearchInCloud = (documentType: string) => {
    const cloudDoc = cloudFiles.findLast((file) => file.type === documentType);
    if (!cloudDoc) {
      return;
    }

    if (applicationId) {
      clearLufeDocTombstone(applicationId, documentType);
    }

    const alreadyInFiles = files.some((f) => f?.type === documentType);
    if (!alreadyInFiles) {
      setFiles((prev) => [
        ...prev,
        {
          file: cloudDoc.documentId,
          file_name: cloudDoc.documentId,
          type: cloudDoc.type,
          is_basic_data: true,
        } as any,
      ]);
      setHasUnsavedChanges(true);
    }

    downloadCloudDocument(cloudDoc.documentId);
  };

  const handleExtraFileChange = (file: File) => {
    setIsContinueDisabled(true);
    const id = generateId();
    const newErrors = [...extraFilesErrors, 'El tipo de documento es requerido'];
    setExtraFilesErrors(newErrors);

    const newDoc: LocalDocument = {
      id,
      type: '',
      label: '',
      file_name: file?.name,
      asset_file_path: file?.name,
      file,
      isPending: true,
      isDeleted: false,
      order: extraFiles.length ? Math.max(...extraFiles.map((f) => f.order || 0)) + 1 : 0,
    };

    setExtraFiles((prev) => [...prev, newDoc]);
  };

  const isApplicationEditable = () => {
    return !isApproved && !isFinished && !isRejected && !isInCommittee;
  };

  const handleEntityChange = (e: string, index: number) => {
    const newLegalEntityId = otherLegalEntities.find(
      (item) => item.description === e
    )?.code;

    const newFiles = [...extraFiles];
    const newErrors = [...extraFilesErrors];

    newErrors[index] = null;
    setExtraFilesErrors(newErrors);

    newFiles[index] = {
      ...(newFiles[index] as any),
      type: newLegalEntityId,
    };
    setExtraFiles(newFiles);

    const hasFilesWithoutType = newFiles.some((f) => (f as any)?.type === null);
    setIsContinueDisabled(hasFilesWithoutType);
  };

  const handleExtraFileDelete = async (index: number) => {
    const doc = extraFiles[index];

    const isPending =
      doc?.isPending ||
      doc?.file?.name?.toString().startsWith('temp-') ||
      (!doc?.asset_file_path && doc?.file);

    if (!isPending && doc?.asset_file_path) {
      const filename = doc.asset_file_path;
      if (applicationId) {
        const response = await deleteApplicationFile(
          cuit,
          applicationId,
          filename,
          doc.type
        );
        if (!response.success) {
          useToast.error({
            title: 'Error al eliminar el documento',
            id: 'documents-delete-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
          return;
        }
      } else {
        try {
          await deleteClientDocument(cuit, filename);
        } catch (error) {
          console.warn('[Documents] Failed to delete extra file:', { filename, error });
          useToast.error({
            title: 'Error al eliminar el documento',
            id: 'documents-delete-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
          return;
        }
      }
      setDeletedKeys((prev) => [...prev, doc.asset_file_path!]);
    }

    const dedupKey = buildDocumentDedupKey(
      doc?.type,
      doc?.asset_file_path || doc?.file_name
    );

    setBulkUploadedFiles((prev) =>
      prev.filter(
        (b) =>
          b.id !== doc?.id &&
          buildDocumentDedupKey(b.type, b.uploadedFileName || b.fileName) !== dedupKey
      )
    );

    const newFiles = extraFiles.filter((_, i) => i !== index);
    const newErrors = extraFilesErrors.filter((_, i) => i !== index);
    setExtraFiles(newFiles);
    setExtraFilesErrors(newErrors);

    const hasFilesWithoutType = newFiles.some((f) => !f.type);
    setIsContinueDisabled(hasFilesWithoutType);
    setHasUnsavedChanges(true);
  };

  // Custom uploader: no pega al backend, devuelve nombre temporal
  const handleBulkFileUpload = async (
    file: File,
    type: string,
    label: string
  ): Promise<{ success: boolean; data?: string; message?: string }> => {
    const fileName = removeFileExtension(file.name);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const tempFileName = `temp-${type || '000'}-${timestamp}-${fileName}`;

    return {
      success: true,
      data: tempFileName,
    };
  };

  const handleBulkFilesUploaded = (uploadedFiles: FileWithType[]) => {
    setBulkUploadedFiles(uploadedFiles);

    // Reflejar en la lista de extraFiles los archivos ya cargados con etiqueta
    const successfulFiles = uploadedFiles.filter(
      (f) => f.uploadStatus === 'success' && f.type
    );

    if (successfulFiles.length > 0) {
      setExtraFiles((prev) =>
        reconcileDocuments({
          base: [],
          locals: prev,
          updates: successfulFiles.map((f, idx) => ({
            id: f.id,
            file: f.file,
            file_name: f.uploadedFileName || f.fileName,
            asset_file_path: f.uploadedFileName || f.fileName,
            type: f.type!,
            label: f.label || otherLegalEntities.find((o) => o.code === f.type)?.description || '',
            isPending: true,
            isDeleted: false,
            order: prev.length + idx,
          })) as LocalDocument[],
        })
      );

      // Ajustar array de errores al largo final
      setExtraFilesErrors((prev) => {
        const targetLength = Math.max(prev.length, extraFiles.length + successfulFiles.length);
        const next = [...prev];
        while (next.length < targetLength) next.push('');
        return next;
      });
    }

    // Validar si hay archivos sin tipo asignado (excluyendo errores)
    const filesPendingType = uploadedFiles.filter(
      (f) => f.uploadStatus !== 'error' && !f.type
    );

    setIsContinueDisabled(filesPendingType.length > 0);
    setHasUnsavedChanges(true);
  };

  const uploadPendingExtraFiles = async (): Promise<{
    files: LocalDocument[];
    ok: boolean;
  }> => {
    const updated = [...extraFiles];
    let ok = true;
    for (let i = 0; i < updated.length; i++) {
      const doc = updated[i];
      if (!doc.isPending || doc.isDeleted) continue;
      if (!doc.file || !doc.type) continue;

      const allExistingFiles = [
        ...(documents || []),
        ...files.filter(Boolean),
        ...extraFiles.filter((f) => !f.isPending && !f.isDeleted && f.file_name),
      ];

      await deletePreviousFileBestEffort(
        doc.file?.name || doc.file_name || '',
        doc.type,
        allExistingFiles
      );

      const formData = new FormData();
      formData.append('file', doc.file);
      formData.append('type', doc.type);
      formData.append('fileName', removeFileExtension(doc.file?.name || doc.file_name || ''));
      formData.append('isBasicData', 'false');

      const response = applicationId
        ? await saveApplicationDocument(cuit, applicationId, formData)
        : await saveClientDocument(cuit, formData);
      if (response.success && response.data) {
        updated[i] = {
          ...doc,
          id: response.data,
          isPending: false,
          asset_file_path: response.data,
          file_name: response.data,
        };
      } else {
        ok = false;
      }
    }
    setExtraFiles(updated);
    setBulkUploadedFiles([]);
    return { files: updated, ok };
  };

  const getFilesToSave = (
    currentExtraFiles = extraFiles,
    currentBulkUploadedFiles = bulkUploadedFiles
  ) => {
    const toDocumentData = (doc: any, forceBasic?: boolean): documentData | null => {
      const type = doc?.type;
      const fileName = doc?.asset_file_path || doc?.file_name;
      if (!type || !fileName) return null;
      return {
        file: fileName,
        file_name: fileName,
        type: String(type),
        is_basic_data: forceBasic ?? !!doc?.is_basic_data,
      };
    };

    const isValidExtra = (f: any) =>
      f &&
      !f.isDeleted &&
      f.type &&
      (f.asset_file_path || f.file_name || f.file) &&
      !isInvalidExtraFile(f);

    const cleanExtraFiles = currentExtraFiles.filter(isValidExtra);

    const extraDedupKeys = new Set(
      cleanExtraFiles.map((f) =>
        buildDocumentDedupKey(f.type, f.asset_file_path || f.file_name)
      )
    );

    const bulkUploadedFilesData: documentData[] = currentBulkUploadedFiles
      .filter(
        (f) =>
          f.uploadStatus === 'success' &&
          f.uploadedFileName &&
          f.type &&
          !extraDedupKeys.has(
            buildDocumentDedupKey(f.type, f.uploadedFileName)
          )
      )
      .map((f) => ({
        file: f.uploadedFileName || f.fileName,
        file_name: f.uploadedFileName || f.fileName,
        type: f.type || '',
        is_basic_data: false,
      }));

    // Base (requeridos) provenientes de estado files
    const baseDocs: documentData[] = files
      .filter(Boolean)
      .map((f) => toDocumentData(f, f?.is_basic_data ?? true))
      .filter((d): d is documentData => Boolean(d));

    // Extras locales (incluye temp y renombrados)
    const extraDocs: documentData[] = cleanExtraFiles
      .map((f) => toDocumentData(f, false))
      .filter((d): d is documentData => Boolean(d));

    const candidates: documentData[] = [
      ...baseDocs,
      ...extraDocs,
      ...bulkUploadedFilesData,
    ];

    return deduplicateDocumentsForSave(candidates);
  };

  const handleSubmit = async (shouldContinue: boolean) => {
    if (isSubmittingRef.current) {
      return;
    }

    // Validar tipos pendientes
    const missingType =
      extraFiles.some((f) => !f.type && !f.isDeleted) ||
      bulkUploadedFiles.some(
        (f) => f.uploadStatus !== 'error' && (!f.type || f.type === null)
      );
    if (missingType) {
      setIsContinueDisabled(true);
      return;
    }

    isSubmittingRef.current = true;

    try {
      setIsUploadingPending(true);
      const requiredUploadResults = await Promise.allSettled(
        Array.from(requiredUploadPromisesRef.current.values())
      );
      if (requiredUploadResults.some((result) => result.status === 'rejected')) {
        useToast.error({
          title: 'Error al cargar uno o más documentos requeridos',
          id: 'documents-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        return;
      }

      const { files: updatedExtra, ok: uploadsOk } = await uploadPendingExtraFiles();
      if (!uploadsOk || hasUnpersistedExtraDocuments(updatedExtra)) {
        useToast.error({
          title: 'No se pudieron guardar todos los documentos extra',
          id: 'documents-toast',
          description: 'Revisá los archivos pendientes e intentá de nuevo.',
          closeButton: true,
          autoClose: 5000,
        });
        return;
      }
      const filesToSave = getFilesToSave(updatedExtra as any, []);
      onSave({
        files: filesToSave,
        shouldContinue,
        lufeBalanceSourceYear,
      });
      setHasUnsavedChanges(false);
    } finally {
      isSubmittingRef.current = false;
      setIsUploadingPending(false);
    }
  };

  return (
    <Card spacing={{ left: '6' }}>
      <Toast id='documents-toast' />
      <Card display='grid' gap='8px' margin={{ bottom: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Cargar documentación básica
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          La documentación pasará por un sistema de verificación antes de ser aprobada.
        </P>
      </Card>
      {isLoading ? (
        <Card
          margin='3'
          display='flex'
          justifyContent='center'
          height='200px'
          alignItems='center'
        >
          <Spinner size='lg' variant='primary' />
        </Card>
      ) : (
        <Card margin={{ top: '5', bottom: '5' }} width='auto' spacing={'3'}>
          {filteredRequiredDocuments.map((document, index) => (
              <Card
                key={document.id}
                margin={{ bottom: '3' }}
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                width='100%'
                gap='16px'
              >
                <Card display='flex' alignItems='center' gap='16px' width='auto'>
                  <Icon variant='neutral' name='document_outline' />
                  <Card display='flex' flexDirection='column' gap='4px'>
                    <P
                      type='3'
                      withMargin={false}
                      variant='neutral'
                      modifier='strong'
                    >
                      {getOriginationStep2DocumentLabel(
                        document.id,
                        document.value,
                        config.isOriginationAccountantSignatureLabelsEnabled === true
                      )}
                    </P>
                    {document.id === BALANCE_DOCUMENT_ID &&
                      (lufeBalanceSourceYear ?? persistedLufeBalanceSourceYear) != null && (
                        <P type='4' withMargin={false} variant='neutral'>
                          {`Balance LUFE: ${lufeBalanceSourceYear ?? persistedLufeBalanceSourceYear}`}
                        </P>
                      )}
                  </Card>
                </Card>
                {!(
                  isApprovedStatus(applicationStatus) ||
                  isFinishedOrRejectedStatus(applicationStatus)
                ) &&
                  cloudFiles.find((file) => file.type === document.id) && (
                    <Card width='240px'>
                      <Button
                        id={`documents-lufe-button-${index}`}
                        variant='secondary'
                        icon='cloud_outline'
                        iconAlign='right'
                        onClick={() => handleSearchInCloud(document.id)}
                        fullWidth
                        size='lg'
                      >
                        Buscar en la nube
                      </Button>
                    </Card>
                  )}
                <Card width='420px'>
                  {files.find((item) => item?.type === document.id)?.type ? (
                    <Card
                      display='flex'
                      alignItems='center'
                      gap='10px'
                      width='420px'
                    >
                      <Card
                        width='300px'
                        variant='neutral'
                        modifier='inverted'
                        height='46px'
                        spacing={{ left: '1', right: '1' }}
                        borderRadiusAmount='3'
                        border
                        display='flex'
                      >
                        <TruncatedTextWithTooltip
                          text={
                            isLufeCloudFileName(
                              files.find((item) => item?.type === document.id)
                                ?.file_name
                            )
                              ? 'Documento asignado desde LUFE'
                              : files.find((item) => item?.type === document.id)
                                  ?.file_name
                          }
                          style={{ minWidth: 0 }}
                        >
                          <Label variant='neutral' modifier='strong' type='2'>
                            {isLufeCloudFileName(
                              files.find((item) => item?.type === document.id)
                                ?.file_name
                            )
                              ? 'Documento asignado desde LUFE'
                              : files.find((item) => item?.type === document.id)
                                  ?.file_name}
                          </Label>
                        </TruncatedTextWithTooltip>
                      </Card>
                      <Button
                        variant='secondary'
                        size='md'
                        onClick={() => handleDownloadFile(document.id)}
                      >
                        <Icon
                          name='view_outline'
                          variant='neutral'
                          height={44}
                          cursor='pointer'
                        />
                      </Button>
                      <Button
                        variant='secondary'
                        size='md'
                        onClick={() => handleFileDelete(document.id)}
                        disabled={!isApplicationEditable()}
                      >
                        <Icon
                          name='delete_outline'
                          variant='neutral'
                          height={44}
                          cursor='pointer'
                        />
                      </Button>
                    </Card>
                  ) : (
                    <Input
                      id={`file-input-${document.id}`}
                      type='file'
                      size='md'
                      placeholder='Sin archivos seleccionados'
                      icons={{ iconRight: 'upload_outline' }}
                      onChange={(event) => handleFileChange(event, document.id)}
                      accept={
                        isBalancePdfDocumentType(document.id)
                          ? '.pdf,application/pdf'
                          : acceptedFormatsLabel
                      }
                      disabled={!isApplicationEditable()}
                    />
                  )}
                  {errors[index] && (
                    <Span variant='danger' type='2'>
                      {errors[index]}
                    </Span>
                  )}
                </Card>
              </Card>
            ))}
        </Card>
      )}
      <Card display='grid' gap='8px' margin={{ bottom: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Cargar documentación extra
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          La documentación pasará por un sistema de verificación antes de ser
          aprobada.
        </P>
      </Card>
      {/* Mostrar archivos extra existentes ARRIBA del drag and drop */}
      {extraFiles.map((document, index) => (
        <Card key={`extra-file-${index}`}>
          <Card margin={{ top: '3' }}>
            <FileRowContainer>
              <Icon variant='neutral' name='document_outline' />
              <DropdownWrapper>
                <DropDown
                  id='client-legal_entity-dropdown'
                  items={otherLegalEntities.map(item => ({ id: item.code, label: item.description }))}
                  size='md'
                  title={
                    otherLegalEntities.find((item) => item.code === document?.type)
                      ?.description
                      ? otherLegalEntities.find(
                          (item) => item.code === document?.type
                        )?.description
                      : 'Seleccioná el tipo de documento'
                  }
                  onChange={(e) => handleEntityChange(e, index)}
                  disabled={!isApplicationEditable()}
                ></DropDown>
              </DropdownWrapper>
              <FileNameWrapper>
                <Card
                  display='flex'
                  justifyContent='space-between'
                  width='100%'
                  variant='neutral'
                  modifier='inverted'
                  height='46px'
                  alignItems='center'
                  spacing={{ left: '2', right: '2' }}
                  borderRadiusAmount='3'
                  border
                  contain='content'
                >
                  <FileNameLabelWrapper>
                    <TruncatedTextWithTooltip text={document?.file_name}>
                      <Label variant='neutral' modifier='strong' type='2'>
                        {document?.file_name}
                      </Label>
                    </TruncatedTextWithTooltip>
                  </FileNameLabelWrapper>
                  <Icon
                    name='delete_outline'
                    variant='neutral'
                    height={44}
                    cursor='pointer'
                    onClick={() => handleExtraFileDelete(index)}
                    disabled={!isApplicationEditable()}
                  />
                </Card>
              </FileNameWrapper>
            </FileRowContainer>
          </Card>
          <Card margin={{ top: '1', left: '6' }}>
            <Span variant='danger' type='2'>
              {extraFilesErrors[index]}
            </Span>
          </Card>
        </Card>
      ))}
      {extraFiles.length > 0 && (
        <Card margin={{ top: '5' }} />
      )}
      {/* Componente BulkDocumentUploader - Drag & Drop ABAJO */}
      <BulkDocumentUploader
        documentTypes={otherLegalEntities.map(item => ({ id: item.code, label: item.description }))}
        onFilesUploaded={handleBulkFilesUploaded}
        onValidationChange={(hasFilesWithoutType) => {
          if (hasFilesWithoutType) {
            setIsContinueDisabled(true);
          } else {
            recalculateContinueDisabled(pendingRequiredUploadsCountRef.current);
          }
        }}
        maxFileSize={MAX_FILE_SIZE}
        acceptedFormats={acceptedFormats}
        acceptedExtensions={acceptedExtensions}
        disabled={!isApplicationEditable() || isUploadingPending}
        cuit={cuit}
        onFileUpload={handleBulkFileUpload}
      />
      <Card display='flex' justifyContent='space-between' margin={{ top: '5' }}>
        <Card width='200px'>
          <Button
            id='client-button-back'
            size='lg'
            variant='secondary'
            onClick={() => onPreviousClick()}
            fullWidth
          >
            Volver
          </Button>
        </Card>
        <Card display='flex' justifyContent='flex-end' gap='16px' width='420px'>
          <Button
            id='client-button-save'
            size='lg'
            variant='secondary'
            onClick={() => handleSubmit(false)}
            disabled={
              isRejected ||
              isApproved ||
              isFinished ||
              isContinueDisabled ||
              isSaving ||
              isUploadingPending ||
              pendingRequiredUploadsCount > 0
            }
            fullWidth
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
          </Button>
          <Button
            id='client-button-save'
            size='lg'
            variant='primary'
            onClick={() => handleSubmit(true)}
            fullWidth
            disabled={isContinueDisabled || isSaving || isUploadingPending || pendingRequiredUploadsCount > 0}
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Continuar'}
          </Button>
        </Card>
      </Card>
    </Card>
  );
};

export default Documents;